import {
  POLICY_SCOPES,
  POLICY_ACTIONS,
} from "@/src/constants/permissionConstants.js";
import store from "@/src/store/index.js";

export const canPerformAction = (resource, action) => {
  try {
    const policies = store.state.rbac.userPolicies || [];

    if (policies.length === 0) {
      return false;
    }

    const resourceLower = resource.toLowerCase();
    const actionUpper = action.toUpperCase();

    let policy = policies.find(
      (p) =>
        p.resource.toLowerCase() === resourceLower &&
        p.action.toUpperCase() === actionUpper
    );

    if (!policy) {
      const objects = store.state.objects?.objects || [];
      const matchingObject = objects.find((obj) => obj.slug === resourceLower);

      if (matchingObject) {
        // Try with the object's ID
        policy = policies.find(
          (p) =>
            p.resource === matchingObject.id &&
            p.action.toUpperCase() === actionUpper
        );
      }
    }

    if (!policy) {
      return false;
    }

    // USER and TEAM scopes are not currently exposed in the UI, but if the backend
    // returns them, they should still grant access (with record-level restrictions)
    const allowedScopes = [
      POLICY_SCOPES.ALL_RECORDS,
      POLICY_SCOPES.USER,
      POLICY_SCOPES.TEAM_BACKEND,
      POLICY_SCOPES.CONDITIONAL_BACKEND,
    ];

    return allowedScopes.includes(policy.scope);
  } catch (error) {
    console.error("Error checking ABAC permission:", error);
    return false;
  }
};

export const getResourceScope = (resource, action) => {
  try {
    const policies = store.state.rbac.userPolicies || [];

    if (policies.length === 0) {
      return POLICY_SCOPES.NONE_OF_THE_RECORDS;
    }

    const resourceLower = resource.toLowerCase();
    const actionUpper = action.toUpperCase();

    // Try direct match first
    let policy = policies.find(
      (p) =>
        p.resource.toLowerCase() === resourceLower &&
        p.action.toUpperCase() === actionUpper
    );

    // If no direct match, try to find by slug
    if (!policy) {
      const objects = store.state.objects?.objects || [];
      const matchingObject = objects.find((obj) => obj.slug === resourceLower);

      if (matchingObject) {
        policy = policies.find(
          (p) =>
            p.resource === matchingObject.id &&
            p.action.toUpperCase() === actionUpper
        );
      }
    }

    return policy?.scope || POLICY_SCOPES.NONE_OF_THE_RECORDS;
  } catch (error) {
    console.error("Error getting resource scope:", error);
    return POLICY_SCOPES.NONE_OF_THE_RECORDS;
  }
};

export const canView = (resource) => {
  return canPerformAction(resource, POLICY_ACTIONS.VIEW);
};

export const canEdit = (resource) => {
  return canPerformAction(resource, POLICY_ACTIONS.EDIT);
};

export const canDelete = (resource) => {
  return canPerformAction(resource, POLICY_ACTIONS.DELETE);
};

export const canExport = (resource) => {
  return canPerformAction(resource, POLICY_ACTIONS.EXPORT);
};

export const canImport = (resource) => {
  return canPerformAction(resource, POLICY_ACTIONS.IMPORT);
};

export const canCreate = (resource) => {
  return canPerformAction(resource, POLICY_ACTIONS.CREATE);
};

export const isEnabled = (resource) => {
  // For non-CRM resources, check ENABLED action first, fallback to VIEW for backward compatibility
  return (
    canPerformAction(resource, POLICY_ACTIONS.ENABLED) ||
    canPerformAction(resource, POLICY_ACTIONS.VIEW)
  );
};
